// src/utils/axiosConfig.ts
import axios from 'axios';

// Same-origin API base. The app always calls "/api" on whatever host it was
// loaded from (localhost or a forwarded/tunnelled origin), and the dev server
// proxy (src/setupProxy.js) relays that to the backend. This is what lets a
// single forwarded port (3000) serve both frontend and backend remotely — no
// absolute backend URL, no second tunnel.
//
// REACT_APP_API_URL still overrides if you need a fixed backend origin (e.g. a
// production deployment where the API lives on a different host).
function resolveApiBaseUrl(): string {
  return process.env.REACT_APP_API_URL || '/api';
}

export const API_BASE_URL = resolveApiBaseUrl();

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for CSRF protection
});

// Request interceptor - attach token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // CSRF token protection
    const csrfToken = getCsrfTokenFromCookies();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Phase 16 — silent session refresh.
//
// The backend has rotating refresh tokens (POST /sessions/issue + /refresh).
// Storage policy mirrors UC-1.2's persistence semantics:
//   - Remember Me ON  → refresh token in localStorage (survives restarts)
//   - Remember Me OFF → sessionStorage (silent refresh mid-session only)
// When no refresh token exists, behavior is exactly the pre-Phase-16
// hard-logout redirect.
// ---------------------------------------------------------------------------

const REFRESH_TOKEN_KEY = 'refreshToken';

export function getStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeRefreshToken(token: string, persistent: boolean): void {
  try {
    if (persistent) localStorage.setItem(REFRESH_TOKEN_KEY, token);
    else sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch {
    /* storage unavailable — silent refresh simply won't happen */
  }
}

export function clearRefreshToken(): void {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Call right after a successful login to mint the initial refresh token.
 * Fire-and-forget: login UX is unchanged if this fails.
 */
export async function issueRefreshToken(persistent: boolean): Promise<void> {
  try {
    const res = await axiosInstance.post('/sessions/issue', {});
    if (res.data?.refreshToken) {
      storeRefreshToken(res.data.refreshToken, persistent);
      // Remember where a rotated token should be stored on refresh.
      localStorage.setItem('refreshTokenPersistent', persistent ? '1' : '0');
    }
  } catch {
    /* non-fatal — session simply won't silently refresh */
  }
}

// Single-flight: concurrent 401s share one refresh round-trip.
let refreshInFlight: Promise<string | null> | null = null;

async function tryRefreshAccessToken(): Promise<string | null> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_BASE_URL}/sessions/refresh`, { refreshToken }, { withCredentials: true, headers: (() => {
        const csrf = getCsrfTokenFromCookies();
        return csrf ? { 'X-CSRF-Token': csrf } : {};
      })() })
      .then((res) => {
        const newToken: string | undefined = res.data?.token;
        const newRefresh: string | undefined = res.data?.refreshToken;
        if (!newToken) return null;
        localStorage.setItem('token', newToken);
        const expiry = getTokenExpiryTime(newToken);
        if (expiry) localStorage.setItem('tokenExpiry', String(expiry));
        if (newRefresh) {
          const persistent = localStorage.getItem('refreshTokenPersistent') !== '0';
          clearRefreshToken();
          storeRefreshToken(newRefresh, persistent);
        }
        return newToken;
      })
      .catch(() => {
        clearRefreshToken();
        return null;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

// Response interceptor — session-expiry handling.
//
// Only a DEAD SESSION may log the user out:
//   - 401 (missing/revoked token), or
//   - 403 whose body is exactly the token-expiry error from authenticateToken
//     ("Invalid or expired token" — expired JWTs surface as 403, not 401).
// Every OTHER 403 (plan limit, consent required, CSRF, account lockout) is a
// feature-level refusal: it must reach the calling component so it can show
// its own message. It must NEVER log the user out — clicking a gated
// feature used to bounce users to /login, which was a bug.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const body = error.response?.data;
    const original = error.config || {};

    const isExpiredTokenSignal =
      status === 401 ||
      (status === 403 && typeof body?.error === 'string' && body.error === 'Invalid or expired token');

    if (!isExpiredTokenSignal) {
      // Feature-level refusal (403 consent/plan/CSRF, or any other error):
      // surface to the caller, keep the session intact.
      return Promise.reject(error);
    }

    // Phase 16.1 — try ONE silent refresh and replay the original request.
    if (
      !original.__isRetryAfterRefresh &&
      !String(original.url || '').includes('/sessions/refresh') &&
      getStoredRefreshToken()
    ) {
      const newToken = await tryRefreshAccessToken();
      if (newToken) {
        original.__isRetryAfterRefresh = true;
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
        return axiosInstance.request(original);
      }
    }

    // Session is genuinely dead. Don't bounce while the user is on a public
    // auth page; let the page surface the error so the toast actually appears.
    const path = window.location.pathname;
    const onAuthPage =
      path.startsWith('/login') ||
      path.startsWith('/register') ||
      path.startsWith('/signup') ||
      path.startsWith('/forgot-password') ||
      path.startsWith('/reset-password') ||
      path.startsWith('/verify');
    if (!onAuthPage) {
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiry');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Extract CSRF token from cookies
 * The token is set by the backend via the csurf middleware
 * Cookie name is typically 'XSRF-TOKEN'
 */
export const getCsrfTokenFromCookies = (): string | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'XSRF-TOKEN') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

// Helper function to check if token is expired
export const isTokenExpired = (): boolean => {
  const expiry = localStorage.getItem('tokenExpiry');
  if (!expiry) return true;
  return Date.now() > parseInt(expiry);
};

// Helper function to get token expiry time from JWT
export const getTokenExpiryTime = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
};
